import React, { useState } from "react";
import PropTypes from "prop-types";
import { AudioPlayer } from "@brivity/ai-components";
import AiCallSummary from "@shared/AiAssistant/AiCallSummary";
import TextToggle from "@shared/v2/TextToggle";
import Avatar from "@shared/v2/Avatar";
import Pagination from "@shared/v2/Pagination";
import ErrorBoundary from "@shared/v2/ErrorBoundary";
import TextButton from "@shared/v2/TextButton";
import Voicemail from "@shared/AiAssistant/Voicemail";
import {
  ChevronDown,
  ChevronRight,
  PlaySolidV6,
  PhoneMissedSolidV6,
  PhoneArrowDownLeftSolidV6,
} from "@shared/v2/Icomoon";
import TableHeader from "./TableHeader";
import TableCell from "./TableCell";
import Arrows from "./Arrows";
import { durationDisplay } from "./helpers";
import { Section } from "../../Reports/components/BusinessDashboard";
import ReportsFilters from "./ReportsFilters";

const LOGS_OPTIONS = [
  { label: "Calls", value: "calls" },
  { label: "Texts", value: "texts" },
];

const TrackingNumbersLogs = ({
  agents,
  isAdmin,
  sort,
  setSort,
  offset,
  textoffset,
  setOffset,
  setTextOffset,
  logsCount,
  textlogsCount,
  textLogs,
  callLogs,
  selectedTrackingNumbers,
  trackingNumbers,
  handleTrackingNumberFilterOnChange,
  minDate,
  maxDate,
  setMinDate,
  setMaxDate,
  chosenView,
  logFilters,
  setLogFilters,
}) => {
  const [selectedLog, setSelectedLog] = useState("calls");
  const handleSort = (field) => {
    let sortDirection = sort[1];

    if (field === sort[0]) {
      sortDirection = sortDirection === "asc" ? "desc" : "asc";
    }

    setSort([field, sortDirection]);
  };

  const changeSelectedLog = (val) => {
    setSelectedLog(val);
  };
  return (
    <div className="tw-flex tw-flex-col tw-items-stretch tw-flex-wrap tw-gap-8px tw-bg-tinted-gray-50">
      <ReportsFilters
        selectedTrackingNumbers={selectedTrackingNumbers}
        trackingNumbers={trackingNumbers}
        handleTrackingNumberFilterOnChange={handleTrackingNumberFilterOnChange}
        minDate={minDate}
        maxDate={maxDate}
        setMinDate={setMinDate}
        setMaxDate={setMaxDate}
        chosenView={chosenView}
        logFilters={logFilters}
        setLogFilters={setLogFilters}
        agents={agents}
        selectedLog={selectedLog}
      />

      <div className="tw-relative tw-bg-white tw-rounded-[8px] tw-border-solid tw-border-[1px] tw-border-neutral-gray-10 bs-xs:tw-p-[15px] tw-flex tw-flex-col tw-p-[32px]">
        <div className="tw-flex tw-flex-col tw-gap-[16px] tw-pb-[32px]">
          <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-[16px]">
            <div className="tw-flex tw-items-center tw-gap-[16px]">
              <TextToggle
                options={LOGS_OPTIONS}
                value={selectedLog}
                onChange={(e) => changeSelectedLog(e.target.value)}
              />
            </div>
          </div>
        </div>
        {selectedLog === "calls" ? (
          <>
            <TrackingNumbersCallLogTable
              logs={callLogs}
              handleSort={handleSort}
              sortField={sort[0]}
              sortDirection={sort[1]}
              totalCount={logsCount}
              currentOffset={offset}
              pageSize={25}
              onChangePage={setOffset}
              isAdmin={isAdmin}
            />
            {callLogs.length === 0 ? (
              <div className="tw-my-[88px] tw-text-18px tw-text-gray-50 tw-text-center">
                No Calls Recorded
              </div>
            ) : (
              ""
            )}
          </>
        ) : (
          <>
            <TrackingNumbersTextLogTable
              logs={textLogs}
              totalCount={textlogsCount}
              currentOffset={textoffset}
              pageSize={25}
              onChangePage={setTextOffset}
              isAdmin={isAdmin}
            />
            {textLogs.length === 0 ? (
              <div className="tw-my-[88px] tw-text-18px tw-text-gray-50 tw-text-center">No Texts</div>
            ) : (
              ""
            )}
          </>
        )}
      </div>
    </div>
  );
};

TrackingNumbersLogs.propTypes = {
  agents: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  sort: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSort: PropTypes.func.isRequired,
  offset: PropTypes.number.isRequired,
  textoffset: PropTypes.number.isRequired,
  setOffset: PropTypes.func.isRequired,
  setTextOffset: PropTypes.func.isRequired,
  logsCount: PropTypes.number.isRequired,
  textlogsCount: PropTypes.number.isRequired,
  callLogs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  textLogs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  selectedTrackingNumbers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  trackingNumbers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handleTrackingNumberFilterOnChange: PropTypes.func.isRequired,
  minDate: PropTypes.string.isRequired,
  maxDate: PropTypes.string.isRequired,
  setMinDate: PropTypes.func.isRequired,
  setMaxDate: PropTypes.func.isRequired,
  chosenView: PropTypes.string.isRequired,
  logFilters: PropTypes.shape().isRequired,
  setLogFilters: PropTypes.func.isRequired,
};

const LogRow = ({ log }) => {
  const [isVisible, setIsVisible] = useState(false);

  return [
    <tr
      className={`tw-h-36px tw-leading-[24px] tw-border-1px tw-border-solid tw-border-x-0 tw-border-gray-15 ${log.voicemailUrl || log.callSid ? "tw-cursor-pointer hover:tw-bg-[#f5f7f7]" : ""}`}
      onClick={() => (log.voicemailUrl || log.callSid) && setIsVisible(!isVisible)}
    >
      <TableCell>
        {log.voicemailUrl || log.callSid ? (
          <span className="tw-text-gray-50">{isVisible ? <ChevronDown /> : <ChevronRight />}</span>
        ) : (
          ""
        )}
      </TableCell>
      <TableCell>
        <a
          href={log.callerUrl}
          className="tw-font-bold tw-text-gray-75 hover:tw-text-gray-75 hover:tw-no-underline"
        >
          {log.callerFullName ? log.callerFullName : log.callerPhone}
        </a>
      </TableCell>
      <TableCell>
        <span className={log.isNumberDeleted ? "tw-text-gray-30" : ""}>
          {log.trackingNumberName}
          {log.isNumberDeleted ? " (Deleted)" : ""}
        </span>
      </TableCell>
      <TableCell>
        {log.answererFullName ? (
          <div className="tw-flex tw-items-center">
            <Avatar
              className="tw-mr-12px"
              size="small"
              src={log.answererAvatarUrl}
              alt={log.answererFullName}
            />
            <span>{log.answererFullName}</span>
          </div>
        ) : (
          "--"
        )}
      </TableCell>
      <TableCell>
        {log.outcome === "Talked" && <TalkedOutcome outcome={log.outcome} />}
        {log.outcome === "Missed" && <MissedOutcome outcome={log.outcome} />}
        {log.outcome === "Voicemail" && <VoicemailOutcome outcome={log.outcome} />}
      </TableCell>
      <TableCell>{log.duration === 0 ? "--" : durationDisplay(log.duration)}</TableCell>
      <TableCell>{log.createdAt}</TableCell>
    </tr>,
    isVisible ? (
      <tr className={isVisible ? "" : "tw-hidden"}>
        <td colSpan={7}>
          {log.outcome === "Voicemail" ? (
            <div className="tw-bg-gray-10 tw-p-[24px]">
              <Section className="tw-bg-white tw-p-24px">
                <Voicemail callSid={log.callSid} audioUrl={log.voicemailUrl} />
              </Section>
            </div>
          ) : (
            <div className="tw-bg-gray-10 tw-p-[24px]">
              <Section className="tw-bg-white tw-p-24px">
                <AiCallSummary callSid={log.callSid} />
              </Section>
            </div>
          )}
        </td>
      </tr>
    ) : null,
  ];
};

const TextLogRow = ({ log }) => {
  const [isVisible, setIsVisible] = useState(false);
  const isExpandable = log?.message && log?.message?.length > 70;

  const expandedClassName = isVisible ? "!tw-align-middle !tw-h-[38px]" : "";
  const expandedTdClassName = isVisible ? "!tw-align-top" : "";

  return [
    <tr
      className={`tw-align-middle tw-text-[14px] tw-border-1px tw-border-solid tw-border-x-0 tw-border-gray-10  ${isExpandable ? "tw-cursor-pointer hover:tw-bg-[#f5f7f7]" : ""}`}
      onClick={() => isExpandable && setIsVisible(!isVisible)}
    >
      <TableCell className={`${expandedTdClassName}`}>
        <div className={`${expandedClassName} ${isVisible ? "!tw-pt-[8.5px]" : ""}`}>
          {isExpandable ? (
            <span className="tw-text-gray-50">{isVisible ? <ChevronDown /> : <ChevronRight />}</span>
          ) : (
            ""
          )}
        </div>
      </TableCell>
      <TableCell className={`${expandedTdClassName}`}>
        <div className={`${expandedClassName} ${isVisible ? "!tw-pt-[8.5px]" : ""}`}>
          <TextButton
            className="tw-p-0"
            size="medium"
            onClick={(e) => {
              e.stopPropagation();
              if (log.leadUrl) {
                window.open(log.leadUrl, "_blank");
              }
            }}
          >
            {log.phoneNumber || log.leadFullName || ""}
          </TextButton>
        </div>
      </TableCell>
      <TableCell className={`${expandedTdClassName}`}>
        <div
          className={`${expandedClassName} ${isVisible && !log.trackingPhoneNumber ? "!tw-pt-[8.5px]" : ""}`}
        >
          <span className={`tw-text-[12px] ${log.isNumberDeleted ? "tw-text-gray-30" : ""}`}>
            {log.trackingNumberName}
            {log.isNumberDeleted ? " (Deleted)" : ""}
            <div>{log.trackingPhoneNumber || ""}</div>
          </span>
        </div>
      </TableCell>
      <TableCell className={`${expandedTdClassName}`}>
        <div className={`${expandedClassName} ${isVisible ? "!tw-pt-[6.5px]" : ""}`}>
          {log.recipientFullName ? (
            <div className="tw-flex tw-items-center">
              <Avatar
                className="tw-mr-12px"
                size="small"
                src={log.recipientAvatarUrl}
                alt={log.recipientFullName}
              />
              <span>{log.recipientFullName}</span>
            </div>
          ) : (
            "--"
          )}
        </div>
      </TableCell>
      <TableCell className="tw-max-w-[500px]">
        <div
          className={` ${isVisible ? "!tw-pt-[8.5px] !tw-whitespace-normal" : "tw-overflow-ellipsis tw-whitespace-nowrap tw-overflow-hidden"}`}
        >
          {log.message || ""}
        </div>
      </TableCell>
      <TableCell className={`${expandedTdClassName}`}>
        <div className={`${expandedClassName}`}>
          <span className="tw-text-[12px]">
            {log?.createdAtDate}
            <div>{log?.createdAtTime}</div>
          </span>
        </div>
      </TableCell>
    </tr>,
  ];
};

const TrackingNumbersCallLogTable = ({
  logs,
  handleSort,
  sortField,
  sortDirection,
  totalCount,
  currentOffset,
  pageSize,
  onChangePage,
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  const currentPage = currentOffset ? currentOffset / pageSize + 1 : 1;
  const nextPage = currentPage === totalPages ? currentPage : currentPage + 1;
  const previousPage = currentPage === 1 ? currentPage : currentPage - 1;

  const onGoToPage = (pageNumber) => {
    const newOffset = (pageNumber - 1) * pageSize;
    onChangePage(newOffset);
  };

  return (
    <div>
      <div className="tw-width-auto tw-relative tw-flex tw-flex-col tw-flex-nowrap tw-overflow-auto tw-grow tw-shrink-0">
        <table className="tw-border-collapse tw-overflow-scroll tw-table-auto tw-flex-nowrap">
          <thead>
            <tr className="tw-text-12px tw-text-gray-75 tw-border-[1px] tw-border-solid tw-border-b-[1px] tw-border-x-0 tw-border-gray-15">
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <th />
              <TableHeader>Caller</TableHeader>
              <TableHeader>Tracking Number</TableHeader>
              <TableHeader sortable onClick={() => handleSort("user_id")}>
                Answered by <Arrows sortBy={sortField === "user_id" ? sortDirection : null} />
              </TableHeader>
              <TableHeader>Outcome</TableHeader>
              <TableHeader sortable onClick={() => handleSort("duration")}>
                Duration <Arrows sortBy={sortField === "duration" ? sortDirection : null} />
              </TableHeader>
              <TableHeader sortable onClick={() => handleSort("id")}>
                Date <Arrows sortBy={sortField === "id" ? sortDirection : null} />
              </TableHeader>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <LogRow key={`log-${log.callLogId}`} log={log} />
            ))}
          </tbody>
        </table>
      </div>
      {logs?.length > 0 ? (
        <div className="tw-flex tw-justify-end tw-mt-32px">
          <Pagination
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={previousPage}
            totalPages={totalPages}
            onGoToPage={onGoToPage}
          />
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

TrackingNumbersCallLogTable.propTypes = {
  logs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handleSort: PropTypes.func.isRequired,
  sortField: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  totalCount: PropTypes.number.isRequired,
  currentOffset: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
};

const TrackingNumbersTextLogTable = ({ logs, totalCount, currentOffset, pageSize, onChangePage }) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  const currentPage = currentOffset ? currentOffset / pageSize + 1 : 1;
  const nextPage = currentPage === totalPages ? currentPage : currentPage + 1;
  const previousPage = currentPage === 1 ? currentPage : currentPage - 1;

  const onGoToPage = (pageNumber) => {
    const newOffset = (pageNumber - 1) * pageSize;
    onChangePage(newOffset);
  };

  return (
    <div>
      <div className="tw-width-auto tw-relative tw-flex tw-flex-col tw-flex-nowrap tw-overflow-auto tw-grow tw-shrink-0">
        <table className="tw-border-collapse tw-overflow-scroll tw-table-auto tw-flex-nowrap">
          <thead>
            <tr className="tw-text-12px tw-text-gray-75 tw-border-[1px] tw-border-solid tw-border-b-[1px] tw-border-x-0 tw-border-gray-10">
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <th />
              <TableHeader>Sender</TableHeader>
              <TableHeader>Tracking Number</TableHeader>
              <TableHeader>Recipient</TableHeader>
              <TableHeader>Text</TableHeader>
              <TableHeader>Date</TableHeader>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <TextLogRow key={`log-${log.callLogId}`} log={log} />
            ))}
          </tbody>
        </table>
      </div>
      {logs?.length > 0 ? (
        <div className="tw-flex tw-justify-end tw-mt-32px">
          <Pagination
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={previousPage}
            totalPages={totalPages}
            onGoToPage={onGoToPage}
          />
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

TrackingNumbersTextLogTable.propTypes = {
  logs: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  totalCount: PropTypes.number.isRequired,
  currentOffset: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
};

const ExpandedInfo = ({ recordingLink }) => (
  <div className="tw-bg-gray-10 tw-p-[24px]">
    <Section className="tw-bg-white">
      <ErrorBoundary>
        <AudioPlayer src={recordingLink} />
      </ErrorBoundary>
    </Section>
  </div>
);

ExpandedInfo.propTypes = {
  recordingLink: PropTypes.string.isRequired,
};

const TalkedOutcome = ({ outcome }) => (
  <div className="tw-flex tw-items-center tw-text-[#36b25f]">
    <PhoneArrowDownLeftSolidV6 className="tw-mr-8px tw-w-16px tw-h-16px" />
    {outcome}
  </div>
);

TalkedOutcome.propTypes = {
  outcome: PropTypes.string.isRequired,
};

const MissedOutcome = ({ outcome }) => (
  <div className="tw-flex tw-items-center tw-text-[#e5342e]">
    <PhoneMissedSolidV6 className="tw-mr-8px tw-w-16px tw-h-16px" />
    {outcome}
  </div>
);

MissedOutcome.propTypes = {
  outcome: PropTypes.string.isRequired,
};

const VoicemailOutcome = ({ outcome }) => (
  <div className="tw-flex tw-items-center tw-text-gray-75">
    <PlaySolidV6 className="tw-mr-8px tw-w-16px tw-h-16px" />
    {outcome}
  </div>
);

VoicemailOutcome.propTypes = {
  outcome: PropTypes.string.isRequired,
};

export default TrackingNumbersLogs;
